// #1183 物販 (お土産) レジのカート/照合ロジック。UI (components/retail/) から分離。
import type { MenuItem } from '../store/useStore'

export type RetailCartLine = {
  itemId: string
  name: string
  price: number
  quantity: number
  stock: number | null
  soldOut: boolean
}

/** スキャン/入力コードを menu item に解決する。barcode 一致 → doc id 一致 の順
 * (商品QRラベルは barcode ?? item.id を値にしている — StoreItemCodes)。 */
export function findMenuItemByCode(menu: MenuItem[], code: string): MenuItem | null {
  const c = code.trim()
  if (!c) return null
  return (
    menu.find((m) => (m.barcode || '') === c) ||
    menu.find((m) => m.id === c) ||
    null
  )
}

/** #1379 PR C セット商品（詰め合わせ）の「作れる数」。
 *
 * セット商品は**自分の在庫を持たない**（単品でも売る商品を親子どちらでも数えると
 * 必ず二重計上になるため、在庫の正は常に子に置く）。代わりに、子の在庫から
 * 「あと何セット作れるか」を都度計算する。
 *
 * 子の在庫が未設定（在庫管理していない）なら制約にならないので無視する。
 * 制約になる子が1つも無ければ null（＝作れる数が決まらない＝売り切れ扱いにしない）。
 */
export function buildableSetCount(
  item: Pick<MenuItem, 'components'>,
  menu: MenuItem[],
): number | null {
  const components = item.components || []
  if (components.length === 0) return null
  let limit: number | null = null
  for (const c of components) {
    const child = menu.find((m) => m.id === c.itemId)
    if (!child || child.stock == null || !c.quantity) continue
    const possible = Math.floor(child.stock / c.quantity)
    limit = limit == null ? possible : Math.min(limit, possible)
  }
  return limit
}

/** #1379 PR C 顧客に見せる残数。
 *
 * セット商品は自分の在庫を持たないので、子から計算した「作れる数」を残数として扱う。
 * 通常商品は従来どおり stock をそのまま返す（飲食メニューは components を持たないので
 * 挙動が変わらない）。
 */
export function effectiveStock(
  item: Pick<MenuItem, 'stock' | 'components'>,
  menu: MenuItem[],
): number | null {
  if ((item.components || []).length > 0) return buildableSetCount(item, menu)
  return item.stock ?? null
}

/** #1379 PR C 売り切れか。
 *
 * セット商品は**中身が尽きたら作れない**ので、店主が手で売り切れにしなくても
 * 自動で売り切れになる（子の在庫が正なので、親に soldOut を持たせて同期させると
 * 必ずズレる）。手動の soldOut も従来どおり尊重する。
 */
export function isEffectivelySoldOut(
  item: Pick<MenuItem, 'soldOut' | 'stock' | 'components'>,
  menu: MenuItem[],
): boolean {
  if (item.soldOut) return true
  return effectiveStock(item, menu) === 0
}

/** #1181 follow-up 要発注か (在庫が発注点以下)。
 *
 * 在庫と発注点の**両方**が設定された商品だけが対象 — 発注点はオプトインなので、
 * 未設定の商品を「在庫が少ない」と勝手に判定しない (既存店の見え方を変えない)。
 * サーバ側の通知条件と同じ式にしてあり、画面のバッジとメールがズレないようにする。
 */
export function needsReorder(item: Pick<MenuItem, 'stock' | 'reorderPoint'>): boolean {
  return item.stock != null && item.reorderPoint != null && item.stock <= item.reorderPoint
}

export function addToRetailCart(
  lines: RetailCartLine[],
  item: MenuItem,
  quantity = 1,
): RetailCartLine[] {
  const existing = lines.find((l) => l.itemId === item.id)
  if (existing) {
    return lines.map((l) => (l.itemId === item.id ? { ...l, quantity: l.quantity + quantity } : l))
  }
  return [
    ...lines,
    {
      itemId: item.id,
      name: item.name,
      price: item.price,
      quantity,
      stock: item.stock ?? null,
      soldOut: !!item.soldOut,
    },
  ]
}

export function retailCartTotal(lines: RetailCartLine[]): number {
  return lines.reduce((s, l) => s + l.price * l.quantity, 0)
}

export function retailCartCount(lines: RetailCartLine[]): number {
  return lines.reduce((s, l) => s + l.quantity, 0)
}
